export type ScreeningCheckType =
  | 'expiry'
  | 'date_coherence'
  | 'dates_match'
  | 'standard_version'
  | 'company_name'
  | 'site_match'
  | 'application_match'
  | 'activity_scope'
  | 'cb_recognized'
  | 'accreditor_pairing'
  | 'document_type';

export type ScreeningCheckMethod = 'deterministic' | 'model';

export type ScreeningCheckOutcome = 'pass' | 'flag' | 'not_evaluated' | 'not_applicable';

export type ScreeningAdminResponse = 'accepted' | 'dismissed' | null;

export type ScreeningRunStatus = 'pending' | 'running' | 'complete' | 'failed';

export interface ScreeningCheck {
  id: number;
  checkType: ScreeningCheckType;
  method: ScreeningCheckMethod;
  outcome: ScreeningCheckOutcome;
  confidence: string | null;
  rationale: string;
  details: Record<string, unknown> | null;
  adminResponse: ScreeningAdminResponse;
  adminResponseBy: number | null;
  adminResponseAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScreeningExtraction {
  organizationName: string;
  standard: string;
  standardVersion: string;
  issueDate: string;
  expirationDate: string;
  certificateNumber: string;
  certificationBody: string;
  accreditationBody: string;
  sites: string[];
  activities: string[];
  documentType: string;
  suspectedInjection: boolean;
}

/**
 * Compact summary of a certificate's current screening run, embedded on each row of the
 * admin Company Certifications grid. The full run (extraction, per-check rationales) is
 * fetched by id via `getScreeningRun` when an admin opens the detail dialog.
 */
export interface CurrentScreeningSummary {
  id: number;
  flagCount: number;
  unresolvedFlagCount: number;
  notEvaluatedCount: number;
  createdAt: string;
}

/** The mutually exclusive states the grid's Screening column renders and filters on. */
export type ScreeningState = 'not_screened' | 'needs_review' | 'reviewed' | 'not_evaluated' | 'clean';

export function screeningStateFor(summary: CurrentScreeningSummary | null | undefined): ScreeningState {
  if (!summary) return 'not_screened';
  if (summary.unresolvedFlagCount > 0) return 'needs_review';
  if (summary.flagCount > 0) return 'reviewed';
  if (summary.notEvaluatedCount > 0) return 'not_evaluated';
  return 'clean';
}

export interface ScreeningRun {
  id: number;
  companyCertification: number;
  companyName: string;
  certificationId: number;
  certificationTitle: string;
  adminReviewed: boolean;
  adminApproved: boolean;
  knownSites: string[];
  knownApplications: string[];
  knownServices: string[];
  recordIssueDate: string | null;
  recordExpirationDate: string | null;
  status: ScreeningRunStatus;
  trigger: 'upload' | 'document_replace' | 'manual' | 'backfill';
  triggeredBy: number | null;
  modelVersion: string;
  promptVersion: string;
  documentName: string;
  extraction: ScreeningExtraction | null;
  suspectedInjection: boolean;
  isCurrent: boolean;
  error: string;
  createdAt: string;
  updatedAt: string;
  checks: ScreeningCheck[];
  cbVerificationUrl: string | null;
}
